/**
 * 채팅 타임라인에서 라이브 결과물(PPT·학습지 초안 카드)을 둘 자리.
 *
 * 결과물은 "마지막으로 바꾼 답변" 바로 아래에 그리고, 그 뒤에 온 대화·삽화는 결과물 아래로 이어진다.
 * 대화 문서의 pptLiveAnchor / worksheetLiveAnchor = { messageId, at } 가 정본이고,
 * 이 필드가 없는 옛 대화는 버전 기록(triggerMessageId)과 파일 기록이 붙은 메시지 중 가장 늦은 자리를 쓴다.
 * 어느 것도 없으면 -1 (타임라인 맨 끝, 예전 동작).
 */

const MAX_ANCHOR_ID_LENGTH = 200;

const isAssistantMessage = (message) => Boolean(message) && (
  message.role === 'assistant' || message.sender === 'ai' || message.type === 'ai'
);

const isUserMessage = (message) => Boolean(message) && (
  message.role === 'user' || message.sender === 'user' || message.type === 'user'
);

export const normalizeLiveAnchor = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const messageId = typeof raw.messageId === 'string' ? raw.messageId.trim() : '';
  if (!messageId || messageId.length > MAX_ANCHOR_ID_LENGTH) return null;
  const at = Number(raw.at);
  return { messageId, at: Number.isFinite(at) && at > 0 ? at : 0 };
};

export const makeLiveAnchor = (messageId, now = Date.now()) => normalizeLiveAnchor({ messageId, at: now });

const indexOfMessage = (messages, messageId) => {
  if (!messageId) return -1;
  return messages.findIndex((message) => message?.id === messageId);
};

// 생성 버전의 triggerMessageId 는 요청한 사용자 메시지다 — 결과물은 그 턴의 답변 아래에 둔다.
const settleOnReply = (messages, index) => {
  if (index < 0) return index;
  if (isUserMessage(messages[index]) && isAssistantMessage(messages[index + 1])) return index + 1;
  return index;
};

const indexOfRecordMessage = (messages, recordId) => {
  if (!recordId) return -1;
  return messages.findIndex((message) => (
    Array.isArray(message?.artifacts) && message.artifacts.some((item) => item?.id === recordId)
  ));
};

export const resolveLiveAnchorIndex = (messages, { anchor, versions, records } = {}) => {
  const list = Array.isArray(messages) ? messages : [];
  if (list.length === 0) return -1;

  const normalized = normalizeLiveAnchor(anchor);
  if (normalized) {
    const explicit = indexOfMessage(list, normalized.messageId);
    if (explicit >= 0) return settleOnReply(list, explicit);
  }

  let best = -1;
  (Array.isArray(versions) ? versions : []).forEach((version) => {
    best = Math.max(best, settleOnReply(list, indexOfMessage(list, version?.triggerMessageId)));
  });
  const latestRecord = Array.isArray(records) ? records[0] : null;
  best = Math.max(best, indexOfRecordMessage(list, latestRecord?.id));
  return best;
};

/**
 * 채팅으로 만든 예전 버전을 그 답변 아래에 작은 카드로 남길 자리.
 * 결과물은 마지막으로 바꾼 답변 아래로 옮겨 가므로, 예전 자리에는 "여기서 만든 N번째 버전" 표시만 둔다.
 * 지금 보는 버전·결과물이 있는 자리·버튼으로 만든 버전(요청 메시지 없음)은 빼고, 한 답변에 여러 개면 가장 늦은 것 하나.
 * 반환: [{ key, index, at: -1, version, order }] — placeLiveArtifacts 에 넣으면 같은 자리의 결과물보다 위에 온다.
 */
export const placeVersionMarkers = (messages, versions, { kind, activeVersionId = null, liveIndex = -1 } = {}) => {
  const list = Array.isArray(messages) ? messages : [];
  const byIndex = new Map();
  (Array.isArray(versions) ? versions : []).forEach((version, order) => {
    if (!version?.versionId || version.versionId === activeVersionId) return;
    const index = settleOnReply(list, indexOfMessage(list, version.triggerMessageId));
    if (index < 0 || index === liveIndex) return;
    byIndex.set(index, { version, order });
  });
  return [...byIndex.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([index, { version, order }]) => ({
      key: `version-${kind || 'artifact'}-${version.versionId}`,
      index,
      at: -1,
      version,
      order,
    }));
};

/**
 * 결과물 묶음을 어느 메시지 뒤에 그릴지.
 * entries: [{ key, index, at }] (index -1 = 맨 끝). 같은 자리면 먼저 자리 잡은 것(at 작은 것)이 위.
 * 반환: { byIndex: Map<number, string[]>, atEnd: string[] }
 */
export const placeLiveArtifacts = (messages, entries) => {
  const count = Array.isArray(messages) ? messages.length : 0;
  const byIndex = new Map();
  const atEnd = [];
  const ordered = (Array.isArray(entries) ? entries : [])
    .map((entry, order) => ({ ...entry, order }))
    .filter((entry) => entry && entry.key)
    .sort((a, b) => ((a.at || 0) - (b.at || 0)) || (a.order - b.order));
  ordered.forEach((entry) => {
    const index = Number.isInteger(entry.index) ? entry.index : -1;
    if (index < 0 || index >= count) {
      atEnd.push(entry.key);
      return;
    }
    const keys = byIndex.get(index) || [];
    keys.push(entry.key);
    byIndex.set(index, keys);
  });
  return { byIndex, atEnd };
};

/**
 * 메시지 줄(turns)과 결과물 묶음(blocks[key])을 placement 대로 한 배열에 섞는다.
 * follower: { afterKey, element } — 그 묶음 바로 뒤에 붙는 카드(다음 도구 넛지). 묶음이 없으면 맨 끝.
 */
export const interleaveLiveBlocks = (turns, placement, blocks, follower = null) => {
  const byIndex = placement?.byIndex instanceof Map ? placement.byIndex : new Map();
  const atEnd = Array.isArray(placement?.atEnd) ? placement.atEnd : [];
  const timeline = [];
  let followerPlaced = false;
  const pushBlock = (key) => {
    if (!blocks?.[key]) return;
    timeline.push(blocks[key]);
    if (follower?.element && !followerPlaced && follower.afterKey === key) {
      timeline.push(follower.element);
      followerPlaced = true;
    }
  };
  (Array.isArray(turns) ? turns : []).forEach((turn, index) => {
    timeline.push(turn);
    (byIndex.get(index) || []).forEach(pushBlock);
  });
  atEnd.forEach(pushBlock);
  if (follower?.element && !followerPlaced) timeline.push(follower.element);
  return timeline;
};
